import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import WCButton from "./WCButton";
import { useEffect } from "react";

export const CartDrawer = () => {
  const { items, total, currency, drawerOpen, setDrawerOpen, updateLine, removeLine, count, goToCheckout } =
    useCart();

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  return (
    <>
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-[150] transition-opacity"
          onClick={() => setDrawerOpen(false)}
          data-testid="cart-drawer-backdrop"
        />
      )}
      <aside
        className={`fixed top-0 right-0 bottom-0 w-full md:w-[460px] bg-black border-l border-white/20 z-[160] transform transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
        data-testid="cart-drawer"
        aria-hidden={!drawerOpen}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/20">
          <div className="wc-heading text-white text-2xl tracking-widest">
            CART <span className="text-white/50 ml-2">({count})</span>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-white"
            data-testid="cart-drawer-close"
            aria-label="Close cart"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-10 text-center text-white/60">
              <div className="wc-label text-xs mb-4">YOUR CART IS EMPTY</div>
              <p className="text-sm mb-8">Nothing to question yet.</p>
              <WCButton as="link" to="/shop" onClick={() => setDrawerOpen(false)}>
                Shop Now
              </WCButton>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {items.map((i) => (
                <div key={i.id} className="flex gap-4 p-5" data-testid={`cart-item-${i.id}`}>
                  <Link
                    to={`/shop/${i.product.handle}`}
                    onClick={() => setDrawerOpen(false)}
                    className="w-20 h-20 bg-[#0a0a0a] shrink-0 overflow-hidden"
                  >
                    {i.product.image && (
                      <img
                        src={i.product.image}
                        alt={i.product.name}
                        className="wc-image w-full h-full object-cover"
                      />
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/shop/${i.product.handle}`}
                      onClick={() => setDrawerOpen(false)}
                      className="wc-heading text-white text-lg tracking-widest leading-tight block"
                    >
                      {i.product.name}
                    </Link>
                    <div className="text-xs text-white/60 mt-1 wc-label">SIZE {i.size}</div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/30">
                        <button
                          className="px-2 py-1 text-white hover:bg-white hover:text-black"
                          onClick={() => updateLine(i.id, i.quantity - 1)}
                          data-testid={`cart-dec-${i.id}`}
                          aria-label="Decrease"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-3 text-sm text-white wc-label">{i.quantity}</span>
                        <button
                          className="px-2 py-1 text-white hover:bg-white hover:text-black"
                          onClick={() => updateLine(i.id, i.quantity + 1)}
                          data-testid={`cart-inc-${i.id}`}
                          aria-label="Increase"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="text-white text-sm">${i.subtotal.toFixed(2)}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeLine(i.id)}
                    className="text-white/50 hover:text-white self-start"
                    data-testid={`cart-remove-${i.id}`}
                    aria-label="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/20 p-6 space-y-4">
            <div className="flex justify-between wc-label text-sm">
              <span className="text-white/60">SUBTOTAL</span>
              <span className="text-white">
                ${total.toFixed(2)} {currency}
              </span>
            </div>
            <p className="text-white/40 text-xs wc-label">
              Shipping + taxes calculated at checkout.
            </p>
            <Link
              to="/cart"
              onClick={() => setDrawerOpen(false)}
              className="wc-btn w-full"
              data-testid="cart-view-full"
            >
              View Cart
            </Link>
            <button
              onClick={goToCheckout}
              className="wc-btn wc-btn--filled w-full"
              data-testid="cart-checkout"
            >
              Checkout
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
